import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Params, Router } from '@angular/router';
import { Cart } from '@core/models/cart/cart.model';
import { CartStoreService } from '@core/services/cart/cart-store.service';
import { CartService } from '@core/services/cart/cart.service';
import { ConfigService } from '@core/services/config/config.service';
import { StoreService } from '@core/services/store/store.service';
import { Browser, isBrowser, SsrService } from '@interticket/core';
import { showSearchBarAnimation } from '@shared/animations/show-searchbar-animation';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  animations: [showSearchBarAnimation],
})
export class HeaderComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();

  readonly cart$: Observable<Cart> = this.cartStoreService.cart$;
  readonly isAppUpdatedModalOpen$ = this.storeService.isLoadingChunkFailed$;
  readonly isPermissionErrorOpen$ = this.storeService.permissionError$;
  readonly defaultTimer = '00:00:00';

  readonly platformLabelImage: boolean = this.configService.siteConfig.platformLabelImage;
  readonly platformLabel: string[] = this.configService.siteConfig.platformLabel;
  readonly logoUrl: string = this.configService.siteConfig.logoSrc;
  readonly showGiftCardMenu: boolean = this.storeService.partnerConfig.card?.hasActive;

  readonly showBy = isBrowser && this.ssrService.location.href.includes('teatr-maly');

  searchBarForm: UntypedFormGroup;
  showMobileSearchBar: boolean;
  isShowMobileMenu: boolean;
  isModalOpen = false;

  constructor(
    private cartStoreService: CartStoreService,
    private cartService: CartService,
    private formBuilder: UntypedFormBuilder,
    private configService: ConfigService,
    private ssrService: SsrService,
    private router: Router,
    public storeService: StoreService
  ) { }

  ngOnInit(): void {
    this.susbcribeToCartTimer();
    this.searchBarForm = this.createSearchBarForm();
  }

  ngOnDestroy(): void {
    this.closeMobileMenu();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createSearchBarForm(): UntypedFormGroup {
    return this.formBuilder.group({
      searchFilter: ['', [Validators.minLength(3), Validators.maxLength(137)]],
    });
  }

  private showCartTimerExpiredModal(): void {
    this.isModalOpen = true;
  }

  @Browser()
  private susbcribeToCartTimer(): void {
    this.cartStoreService.getCartTimer()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (cartTimer: string) => {
          if (cartTimer === this.defaultTimer) {
            this.showCartTimerExpiredModal();
          }
        },
      });
  }

  onSearch(): void {
    if (this.searchBarForm.invalid) {
      return;
    }
    const queryParams: Params = {
      searchFilter: this.searchBarForm.get('searchFilter').value,
    };

    this.searchBarForm.get('searchFilter').setValue('');
    this.router.navigate(['/event'], { queryParams });
  }

  closeCartTimerExpiredModal(): void {
    this.isModalOpen = false;
    this.cartService.getCart()
      .pipe(take(1))
      .subscribe({
        error: (e) => {
          console.error(e);
          this.router.navigate(['error', 'server-error']);
        },
      });
  }

  @Browser()
  closeAppUpdatedModal(reload?: boolean): void {
    this.storeService.isLoadingChunkFailed = false;
    if (reload) {
      this.ssrService.location.reload();
    }
  }

  @Browser()
  closePermissionErrorModal(reload?: boolean): void {
    this.storeService.permissionError = false;
    if (reload) {
      this.storeService.guestToken = { ...this.storeService.guestToken, accessToken: null };
      this.ssrService.location.reload();
    }
  }

  @Browser()
  showMobileMenu(): void {
    this.isShowMobileMenu = true;
    // disable scroll on opening sidebar
    this.ssrService.document.body.style.overflow = 'hidden';
  }

  @Browser()
  closeMobileMenu(): void {
    this.isShowMobileMenu = false;
    // enable scroll on closing sidebar
    this.ssrService.document.body.style.overflow = 'auto';
  }

}
